import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Trophy, Calendar, Target, Plus, BarChart3, Pencil, Trash2, RotateCcw, Eye, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { getOrganizationCompetitions, updateCompetition, deleteCompetition, resetCompetitionLogs } from '@/lib/firebase/queries/competitions';
import { isResetConfirmed } from '@/lib/competitions/resetConfirmation';
import type { Competition } from '@/types/competitions';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CreateCompetitionDialog } from './CreateCompetitionDialog';
import { EditCompetitionDialog } from './EditCompetitionDialog';
import { CompetitionDetailsDialog } from './CompetitionDetailsDialog';
import { CopyCompetitionDialog, type CopyTargetOrganization } from './CopyCompetitionDialog';
import { TopPerformersWidget } from '../TopPerformersWidget';

interface CompetitionsListProps {
  organizationId: string;
  organizationName?: string;
  /** Organizations a copy can go to (the manager's own); defaults to this one. */
  organizations?: CopyTargetOrganization[];
}

export function CompetitionsList({ organizationId, organizationName, organizations }: CompetitionsListProps) {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState<Competition | null>(null);
  const [editingCompetition, setEditingCompetition] = useState<Competition | null>(null);
  const [deletingCompetition, setDeletingCompetition] = useState<Competition | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [resettingCompetition, setResettingCompetition] = useState<Competition | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetTyped, setResetTyped] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [togglingPublishId, setTogglingPublishId] = useState<string | null>(null);
  // Read-only details (namat#26). By id, so a refetch shows fresh data.
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [copyingCompetition, setCopyingCompetition] = useState<Competition | null>(null);
  const { t, dir } = useLanguage();

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const data = await getOrganizationCompetitions(organizationId);
      setCompetitions(data);
    } catch (error) {
      console.error('Error fetching competitions:', error);
      toast.error('Failed to load competitions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchCompetitions();
    }
  }, [organizationId]);

  const getStatusColor = (status: Competition['status']) => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: Competition['status']) => {
    switch (status) {
      case 'upcoming':
        return t('dashboard.manager.competitions.status.upcoming') || 'Upcoming';
      case 'active':
        return t('dashboard.manager.competitions.status.active') || 'Active';
      case 'completed':
        return t('dashboard.manager.competitions.status.completed') || 'Completed';
      default:
        return status;
    }
  };

  const handleShowResults = (competition: Competition) => {
    setSelectedCompetition(competition);
    setShowResultsDialog(true);
  };

  const detailsCompetition = detailsId ? competitions.find(c => c.id === detailsId) ?? null : null;

  const copyTargets: CopyTargetOrganization[] = organizations?.length
    ? organizations
    : [{ id: organizationId, name: organizationName || organizationId }];

  const handleEdit = (competition: Competition) => {
    setEditingCompetition(competition);
    setShowEditDialog(true);
  };

  const handleDeleteClick = (competition: Competition) => {
    setDeletingCompetition(competition);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingCompetition) return;

    try {
      setIsDeleting(true);
      await deleteCompetition(deletingCompetition.id);
      toast.success(t('dashboard.manager.competitions.messages.deletedSuccessfully') || 'Competition deleted successfully');
      setShowDeleteDialog(false);
      setDeletingCompetition(null);
      fetchCompetitions();
    } catch (error) {
      console.error('Error deleting competition:', error);
      toast.error(t('dashboard.manager.competitions.messages.deleteFailed') || 'Failed to delete competition');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResetClick = (competition: Competition) => {
    setResettingCompetition(competition);
    setResetTyped('');
    setShowResetDialog(true);
  };

  // Reset keeps the competition but deletes every participant's completions, so
  // unlike Delete it is gated on typing the competition's name back.
  const handleConfirmReset = async () => {
    if (!resettingCompetition || !isResetConfirmed(resetTyped, resettingCompetition.name)) return;

    try {
      setIsResetting(true);
      const deleted = await resetCompetitionLogs(resettingCompetition.id);
      toast.success(
        (t('dashboard.manager.competitions.messages.resetSuccessfully') || 'Cleared {count} logs.')
          .replace('{count}', String(deleted))
      );
      setShowResetDialog(false);
      setResettingCompetition(null);
      setResetTyped('');
      fetchCompetitions();
    } catch (error) {
      console.error('Error resetting competition logs:', error);
      toast.error(t('dashboard.manager.competitions.messages.resetFailed') || 'Failed to reset the logs');
    } finally {
      setIsResetting(false);
    }
  };

  const isCompetitionPublished = (competition: Competition) => competition.isPublished !== false;

  const handleTogglePublish = async (competition: Competition) => {
    const nextPublished = !isCompetitionPublished(competition);

    try {
      setTogglingPublishId(competition.id);
      await updateCompetition(competition.id, {
        name: competition.name,
        description: competition.description,
        startDate: competition.startDate,
        endDate: competition.endDate,
        habits: competition.habits,
        isPublished: nextPublished,
      });
      toast.success(
        nextPublished
          ? (t('dashboard.manager.competitions.messages.publishSuccess') || 'Competition published')
          : (t('dashboard.manager.competitions.messages.unpublishSuccess') || 'Competition unpublished')
      );
      fetchCompetitions();
    } catch (error) {
      console.error('Error toggling competition publish status:', error);
      toast.error(t('dashboard.manager.competitions.messages.publishToggleFailed') || 'Failed to update publish status');
    } finally {
      setTogglingPublishId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-8 w-20" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={dir}>
      <div className={`flex ${dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'} justify-between items-center`}>
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {t('dashboard.manager.competitions.title') || 'Competitions'}
          </h2>
          <p className="text-muted-foreground">
            {t('dashboard.manager.competitions.pageDescription') || 'Manage competitions for your organization'}
          </p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
        >
          <Plus className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
          {t('dashboard.manager.competitions.createCompetition') || 'Create Competition'}
        </Button>
      </div>

      {competitions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Trophy className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {t('dashboard.manager.competitions.noCompetitions') || 'No competitions yet'}
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              {t('dashboard.manager.competitions.noCompetitionsDesc') || 'Create your first competition to engage your organization members'}
            </p>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('dashboard.manager.competitions.createFirstCompetition') || 'Create Your First Competition'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {competitions.map((competition) => {
            const published = isCompetitionPublished(competition);
            return (
            <Card key={competition.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{competition.name}</CardTitle>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={getStatusColor(competition.status)}>
                        {getStatusText(competition.status)}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={published ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-amber-300 text-amber-700 bg-amber-50'}
                      >
                        {published
                          ? (t('dashboard.manager.competitions.published') || 'Published')
                          : (t('dashboard.manager.competitions.unpublished') || 'Unpublished')}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleEdit(competition)}
                      aria-label={t('dashboard.manager.competitions.editCompetition') || 'Edit Competition'}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setCopyingCompetition(competition)}
                      aria-label={t('dashboard.manager.competitions.copy.title') || 'Copy competition'}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleResetClick(competition)}
                      aria-label={t('dashboard.manager.competitions.resetLogs') || 'Reset logs'}
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteClick(competition)}
                      aria-label={t('dashboard.manager.competitions.deleteCompetition') || 'Delete Competition'}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <Trophy className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {competition.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {competition.description}
                    </p>
                  )}

                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {format(competition.startDate, 'MMM dd')} - {format(competition.endDate, 'MMM dd, yyyy')}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Target className="h-4 w-4" />
                    <span>
                      {competition.habits.length} {competition.habits.length === 1 ? 'habit' : 'habits'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2 pt-1 text-sm">
                    <span className="text-muted-foreground">
                      {t('dashboard.manager.competitions.isPublishedLabel') || 'Visible to beneficiaries'}
                    </span>
                    <Switch
                      checked={published}
                      disabled={togglingPublishId === competition.id}
                      onCheckedChange={() => handleTogglePublish(competition)}
                      aria-label={
                        published
                          ? (t('dashboard.manager.competitions.unpublish') || 'Unpublish')
                          : (t('dashboard.manager.competitions.publish') || 'Publish')
                      }
                    />
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setDetailsId(competition.id)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      {t('dashboard.manager.competitions.viewDetails') || 'View Details'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleShowResults(competition)}
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      {t('dashboard.manager.competitions.showResults') || 'Show Results'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      <CreateCompetitionDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        organizationId={organizationId}
        onSuccess={fetchCompetitions}
      />

      {editingCompetition && (
        <EditCompetitionDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          competition={editingCompetition}
          onSuccess={fetchCompetitions}
        />
      )}

      {detailsCompetition && (
        <CompetitionDetailsDialog
          competition={detailsCompetition}
          organizationName={organizationName}
          open
          onOpenChange={(open) => { if (!open) setDetailsId(null); }}
          statusLabel={getStatusText(detailsCompetition.status)}
          statusClassName={getStatusColor(detailsCompetition.status)}
          published={isCompetitionPublished(detailsCompetition)}
          publishToggling={togglingPublishId === detailsCompetition.id}
          onTogglePublish={() => handleTogglePublish(detailsCompetition)}
          onEdit={() => { setDetailsId(null); handleEdit(detailsCompetition); }}
          onShowResults={() => { setDetailsId(null); handleShowResults(detailsCompetition); }}
          onCopy={() => { setDetailsId(null); setCopyingCompetition(detailsCompetition); }}
        />
      )}

      {copyingCompetition && (
        <CopyCompetitionDialog
          source={copyingCompetition}
          organizations={copyTargets}
          open
          onOpenChange={(open) => { if (!open) setCopyingCompetition(null); }}
          onCopied={(targetId) => { if (targetId === organizationId) fetchCompetitions(); }}
        />
      )}

      {selectedCompetition && (
        <TopPerformersWidget
          competition={selectedCompetition}
          organizationId={organizationId}
          open={showResultsDialog}
          onOpenChange={setShowResultsDialog}
        />
      )}

      {deletingCompetition && (
        <AlertDialog
          open={showDeleteDialog}
          onOpenChange={(open) => {
            setShowDeleteDialog(open);
            if (!open) setDeletingCompetition(null);
          }}
        >
          <AlertDialogContent dir={dir}>
            <AlertDialogHeader>
              <AlertDialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                {t('dashboard.manager.competitions.deleteConfirmTitle') || 'Delete this competition?'}
              </AlertDialogTitle>
              <AlertDialogDescription className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                {t('dashboard.manager.competitions.deleteConfirmDescription', { name: deletingCompetition.name }) ||
                  `This will permanently delete "${deletingCompetition.name}" and all recorded results submitted by members. This action cannot be undone.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>
                {t('dashboard.manager.competitions.cancel') || 'Cancel'}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleConfirmDelete();
                }}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {isDeleting
                  ? (t('dashboard.manager.competitions.deleting') || 'Deleting...')
                  : (t('dashboard.manager.competitions.confirmDelete') || 'Delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {resettingCompetition && (
        <AlertDialog
          open={showResetDialog}
          onOpenChange={(open) => {
            setShowResetDialog(open);
            if (!open) {
              setResettingCompetition(null);
              setResetTyped('');
            }
          }}
        >
          <AlertDialogContent dir={dir}>
            <AlertDialogHeader>
              <AlertDialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                {t('dashboard.manager.competitions.resetConfirmTitle') || 'Reset all logs?'}
              </AlertDialogTitle>
              <AlertDialogDescription className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                {(t('dashboard.manager.competitions.resetConfirmDescription') ||
                  'This deletes every participant\'s completions for "{name}". Standings go back to zero. The competition, its habits and its dates are kept. This cannot be undone.'
                ).replace('{name}', resettingCompetition.name)}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2">
              <p className={`text-sm font-medium ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                {(t('dashboard.manager.competitions.resetTypeName') || 'Type "{name}" to confirm').replace(
                  '{name}',
                  resettingCompetition.name
                )}
              </p>
              <Input
                value={resetTyped}
                onChange={(e) => setResetTyped(e.target.value)}
                disabled={isResetting}
                autoComplete="off"
                dir={dir}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isResetting}>
                {t('dashboard.manager.competitions.cancel') || 'Cancel'}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleConfirmReset();
                }}
                disabled={isResetting || !isResetConfirmed(resetTyped, resettingCompetition.name)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {isResetting
                  ? (t('dashboard.manager.competitions.resetting') || 'Resetting...')
                  : (t('dashboard.manager.competitions.confirmReset') || 'Reset all logs')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
